<template>
  <div>
    <div class="main-content">
      <div class="row">
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">
              <div class="card-title">
                {{ role.id ? $t('role.add.editTitle') : $t('role.add.newTitle') }}
              </div>
            </div>
            <hr style="margin-top: -4px" />
            <div class="card-body">
              <div class="row">
                <div class="col-md-12">
                  <div class="form-row">
                    <div class="form-group col-md-6">
                      <label for="roleName">
                        <span class="required">*</span> {{ $t('role.add.nameLabel') }}
                      </label>
                      <input
                        autocomplete="off"
                        type="text"
                        id="roleName"
                        class="form-control"
                        v-model="role.name"
                        :placeholder="$t('role.add.namePlaceholder')"
                      />
                    </div>
                    <div class="form-group col-md-6">
                      <label>{{ $t('role.add.statusLabel') }}</label>
                      <select class="form-control" v-model="role.status">
                        <option :value="1">{{ $t('common.active') }}</option>
                        <option :value="0">{{ $t('common.inactive') }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <label for="roleDescription">{{ $t('role.add.descriptionLabel') }}</label>
                      <textarea
                        id="roleDescription"
                        class="form-control"
                        v-model="role.description"
                        rows="3"
                        :placeholder="$t('role.add.descriptionPlaceholder')"
                      ></textarea>
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="form-group col-md-12">
                      <div class="custom-control custom-checkbox">
                        <input
                          type="checkbox"
                          class="custom-control-input"
                          id="roleSuperAdmin"
                          v-model="role.is_super_admin"
                        />
                        <label class="custom-control-label" for="roleSuperAdmin">
                          {{ $t('role.add.superAdminLabel') }}
                        </label>
                      </div>
                    </div>
                  </div>

                  <span class="required_fields">
                    <span class="required">*</span>
                    <strong>{{ $t('role.add.requiredFieldsNote') }}</strong>
                  </span>
                  <div class="block text-right">
                    <router-link to="/roles" class="btn btn-back mr-1">
                      <i class="fa fa-arrow-left"></i> {{ $t('common.back') }}
                    </router-link>
                    <a v-if="$hasAbility('rbac.gerenciar')" class="btn btn-add" @click="save">
                      {{ $t('common.save') }} <i class="fa fa-save"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Habilidades -->
        <div class="col-md-12">
          <div class="card">
            <div class="card-header">{{ $t('role.add.abilitiesTitle') }}</div>
            <div class="card-body">
              <div v-if="!role.id" class="text-muted text-center py-3">
                <i class="fa fa-info-circle mr-1"></i>
                {{ $t('role.add.saveFirstNote') }}
              </div>
              <template v-else-if="role.is_super_admin">
                <div class="text-muted text-center py-3">
                  <i class="fa fa-shield-alt mr-1"></i>
                  {{ $t('role.add.superAdminNote') }}
                </div>
              </template>
              <template v-else>
                <div class="form-group node-scope-select">
                  <label>{{ $t('role.add.configureForLabel') }}</label>
                  <v-select
                    :options="nodeOptions"
                    :reduce="(n) => n.value"
                    :clearable="false"
                    v-model="selectedNodeId"
                    class="vselect"
                  />
                  <small v-if="selectedNodeId === null" class="text-muted d-block mt-25">
                    {{ $t('role.add.editingDefaultNote') }}
                  </small>
                  <small v-else-if="nodeHasOverride" class="text-warning d-block mt-25">
                    <i class="fa fa-exclamation-circle mr-25"></i>
                    {{ $t('role.add.nodeOverrideNote') }}
                    <a href="#" @click.prevent="resetNodeAbilities">{{ $t('role.add.restoreDefaultLink') }}</a>
                  </small>
                  <small v-else class="text-muted d-block mt-25">
                    {{ $t('role.add.nodeInheritingNote') }}
                  </small>
                </div>
                <div
                  v-if="!abilities.length"
                  class="text-muted text-center py-3"
                >
                  {{ $t('role.add.noAbilities') }}
                </div>
                <div v-else class="abilities-grid">
                  <div
                    v-for="(group, module) in abilitiesByModule"
                    :key="module"
                    class="ability-module"
                  >
                    <div class="ability-module__header">
                      <div class="ability-module__title">
                        <feather-icon
                          :icon="moduleIcon(module)"
                          size="15"
                          class="mr-50 text-primary"
                        />
                        {{ moduleLabel(module) }}
                      </div>
                      <div class="ability-module__actions">
                        <span class="ability-module__count">
                          {{ selectedInModule(group) }}/{{ group.length }}
                        </span>
                        <button
                          type="button"
                          class="ability-module__toggle"
                          @click="toggleModule(group)"
                        >
                          {{
                            selectedInModule(group) === group.length
                              ? $t('role.add.unmarkAllButton')
                              : $t('role.add.markAllButton')
                          }}
                        </button>
                      </div>
                    </div>
                    <div class="ability-module__body">
                      <div
                        v-for="ability in group"
                        :key="ability.id"
                        class="ability-item"
                      >
                        <div class="custom-control custom-checkbox">
                          <input
                            type="checkbox"
                            class="custom-control-input"
                            :id="'ability_' + ability.id"
                            :value="ability.id"
                            v-model="selectedAbilityIds"
                          />
                          <label
                            class="custom-control-label"
                            :for="'ability_' + ability.id"
                          >
                            {{ ability.description || ability.name }}
                          </label>
                        </div>
                        <code class="ability-item__slug">{{ ability.name }}</code>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="block text-right mt-2">
                  <a v-if="$hasAbility('rbac.gerenciar')" class="btn btn-add" @click="syncAbilities">
                    {{ $t('role.add.saveAbilitiesButton') }} <i class="fa fa-save"></i>
                  </a>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const MODULE_META = {
  despacho: { icon: "SendIcon" },
  escala: { icon: "ClockIcon" },
  node: { icon: "GitBranchIcon" },
  forms: { icon: "ClipboardIcon" },
  rbac: { icon: "ShieldIcon" },
  usuario: { icon: "UserIcon" },
  ocorrencia: { icon: "MapPinIcon" },
  regulacao: { icon: "ActivityIcon" },
  equipe: { icon: "AnchorIcon" },
  profissional: { icon: "UsersIcon" },
  envolvido: { icon: "HeartIcon" },
  supervisao: { icon: "EyeIcon" },
  chat: { icon: "MessageCircleIcon" },
  videowall: { icon: "MonitorIcon" },
  cadastro: { icon: "FileTextIcon" },
  outros: { icon: "GridIcon" },
};

export default {
  data() {
    return {
      role: {
        name: "",
        description: "",
        status: 1,
        is_super_admin: false,
      },
      abilities: [],
      selectedAbilityIds: [],
      defaultAbilityIds: [],
      nodes: [],
      selectedNodeId: null,
      nodeHasOverride: false,
    };
  },
  computed: {
    abilitiesByModule() {
      return this.abilities.reduce((groups, ability) => {
        const mod = ability.module || "outros";
        if (!groups[mod]) groups[mod] = [];
        groups[mod].push(ability);
        return groups;
      }, {});
    },
    nodeOptions() {
      return [
        { label: this.$t("role.add.defaultNodeOption"), value: null },
        ...this.nodes.map((n) => ({ label: n.name, value: n.id })),
      ];
    },
  },
  watch: {
    selectedNodeId() {
      this.loadAbilitiesForScope();
    },
  },
  methods: {
    moduleLabel(module) {
      return MODULE_META[module] ? this.$t(`role.add.modules.${module}`) : module;
    },
    moduleIcon(module) {
      return (MODULE_META[module] && MODULE_META[module].icon) || "GridIcon";
    },
    selectedInModule(group) {
      return group.filter((a) => this.selectedAbilityIds.includes(a.id))
        .length;
    },
    toggleModule(group) {
      const groupIds = group.map((a) => a.id);
      const allSelected = this.selectedInModule(group) === group.length;

      this.selectedAbilityIds = allSelected
        ? this.selectedAbilityIds.filter((id) => !groupIds.includes(id))
        : [...new Set([...this.selectedAbilityIds, ...groupIds])];
    },
    save() {
      const self = this;
      let api = self.$store.state.api + "roles";

      if (self.role.id) {
        self.role._method = "PUT";
        api += "/" + self.role.id;
      }

      self.$loading(true);

      axios
        .post(api, self.role)
        .then((response) => {
          const saved = response.data.data[0] || response.data.data;
          // Atualiza role in-place para exibir o painel de habilidades sem redirect
          self.role = { ...saved };
          // Atualiza URL sem recarregar o componente
          if (self.$route.name !== "EditRole") {
            self.$router.replace("/roles/edit/" + saved.id);
          }
          self.$message(
            self.$t("role.common.successTitle"),
            self.$t("role.add.savedMessage"),
            "success",
          );
          self.$loading(false);
        })
        .catch((error) => {
          self.$loading(false);
          self.$message(null, error.response.data, "error");
        });
    },
    syncAbilities() {
      const self = this;
      const api =
        self.$store.state.api + "roles/" + self.role.id + "/sync-abilities";
      const payload = { ability_ids: self.selectedAbilityIds };
      if (self.selectedNodeId !== null) {
        payload.node_id = self.selectedNodeId;
      }

      self.$loading(true);

      axios
        .post(api, payload)
        .then(() => {
          self.$message(
            self.$t("role.common.successTitle"),
            self.$t("role.add.abilitiesSyncedMessage"),
            "success",
          );
          self.$loading(false);
          if (self.selectedNodeId !== null) {
            self.nodeHasOverride = true;
          }
        })
        .catch((error) => {
          self.$loading(false);
          self.$message(null, error.response.data, "error");
        });
    },
    resetNodeAbilities() {
      const self = this;
      if (self.selectedNodeId === null) return;
      const api =
        self.$store.state.api +
        "roles/" +
        self.role.id +
        "/reset-node-abilities";

      self.$loading(true);

      axios
        .post(api, { node_id: self.selectedNodeId })
        .then(() => {
          self.$message(self.$t("role.common.successTitle"), self.$t("role.add.resetToDefaultMessage"), "success");
          self.loadAbilitiesForScope();
          self.$loading(false);
        })
        .catch((error) => {
          self.$loading(false);
          self.$message(null, error.response.data, "error");
        });
    },
    loadAbilitiesForScope() {
      const self = this;

      if (self.selectedNodeId === null) {
        self.selectedAbilityIds = [...self.defaultAbilityIds];
        self.nodeHasOverride = false;
        return;
      }

      const api =
        self.$store.state.api +
        "roles/" +
        self.role.id +
        "/node-abilities?node_id=" +
        self.selectedNodeId;

      self.$loading(true);

      axios
        .get(api)
        .then((response) => {
          self.selectedAbilityIds = response.data.ability_ids || [];
          self.nodeHasOverride = !!response.data.has_override;
          self.$loading(false);
        })
        .catch((error) => {
          self.$loading(false);
          self.$message(null, error.response.data, "error");
        });
    },
    getNodes() {
      const self = this;
      axios
        .get(self.$store.state.api + "nodes?paginated=false")
        .then((response) => {
          self.nodes = response.data.data || [];
        })
        .catch(() => {});
    },
    getRole(id) {
      const self = this;
      const api = self.$store.state.api + "roles/" + id + "?with[]=abilities";

      self.$loading(true);

      axios
        .get(api)
        .then((response) => {
          self.role = response.data.data[0];
          self.defaultAbilityIds = (self.role.abilities || []).map(
            (a) => a.id,
          );
          self.selectedAbilityIds = [...self.defaultAbilityIds];
          self.$loading(false);
        })
        .catch((error) => {
          self.$loading(false);
          self.$message(null, error.response.data, "error");
        });
    },
    getAbilities() {
      const self = this;
      const api = self.$store.state.api + "abilities?paginated=false";

      axios
        .get(api)
        .then((response) => {
          self.abilities = response.data.data;
        })
        .catch((error) => {
          self.$message(null, error.response.data, "error");
        });
    },
  },
  mounted() {
    const self = this;
    self.getAbilities();
    self.getNodes();
    const id = self.$route.params.id;
    if (id) {
      self.getRole(id);
    }
  },
};
</script>

<style scoped>
.node-scope-select {
  max-width: 420px;
  margin-bottom: 18px;
}

.abilities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.ability-module {
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.ability-module__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 14px;
  background: #f8f8f8;
  border-bottom: 1px solid #e4e7ec;
}

.ability-module__title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
}

.ability-module__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.ability-module__count {
  font-size: 0.8rem;
  color: #8592a3;
  font-variant-numeric: tabular-nums;
}

.ability-module__toggle {
  border: none;
  background: none;
  padding: 0;
  font-size: 0.8rem;
  color: #7367f0;
  cursor: pointer;
  white-space: nowrap;
}

.ability-module__toggle:hover {
  text-decoration: underline;
}

.ability-module__body {
  padding: 4px 14px;
}

.ability-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 0;
}

.ability-item + .ability-item {
  border-top: 1px dashed #eef0f3;
}

.ability-item .custom-control-label {
  font-size: 0.95rem;
}

.ability-item__slug {
  flex-shrink: 0;
  font-size: 0.76rem;
  color: #8592a3;
  background: #f1f2f6;
  padding: 2px 7px;
  border-radius: 4px;
}

/* dark mode */
.dark-layout .ability-module {
  background: #283046;
  border-color: #3b4253;
}

.dark-layout .ability-module__header {
  background: #232a3d;
  border-color: #3b4253;
}

.dark-layout .ability-item + .ability-item {
  border-color: #3b4253;
}

.dark-layout .ability-item__slug {
  background: #161d31;
  color: #b4b7bd;
}
</style>
